import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, AlertTriangle } from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import ReportPageShell from "@/components/reports/ReportPageShell";
import ExportDropdown from "@/components/reports/ExportDropdown";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EntityCombobox } from "@/components/ui/EntityCombobox";
import { useWithBack } from "@/lib/nav";
import StatusBadge from "@/components/StatusBadge";
import { formatDate } from "@/lib/format";

const AGES = [
  { value: "0", label: "All ages" },
  { value: "7", label: "≥ 7 days old" },
  { value: "30", label: "≥ 30 days old" },
  { value: "60", label: "≥ 60 days old" },
];

export default function ActionsOutstandingReport() {
  const withBackHref = useWithBack();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("all");
  const [minAge, setMinAge] = useState("0");
  const [loading, setLoading] = useState(true);
  const [bulkBusy, setBulkBusy] = useState(false);
  const reqIdRef = useRef(0);

  useEffect(() => {
    api.get("/customers", { params: { active: true, limit: 500 } })
      .then(({ data }) => setCustomers(data.items || []))
      .catch((err) => toast.error(formatApiError(err)));
  }, []);

  const load = useCallback(async () => {
    const my = ++reqIdRef.current;
    setLoading(true);
    try {
      const params = {};
      if (customerId !== "all") params.customer_id = customerId;
      if (minAge !== "0") params.min_age_days = minAge;
      const { data } = await api.get("/reports/actions-outstanding", { params });
      if (my !== reqIdRef.current) return;
      setItems(data.items || []);
      setSelected(new Set());
    } catch (err) {
      if (my !== reqIdRef.current) return;
      toast.error(formatApiError(err));
    } finally {
      if (my === reqIdRef.current) setLoading(false);
    }
  }, [customerId, minAge]);

  useEffect(() => { load(); }, [load]);

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const toggleAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map((i) => i.id)));
  };

  const createOne = (row) => {
    const params = new URLSearchParams({
      customer_id: row.customer_id || "",
      site_id: row.site_id || "",
      machine_id: (row.machine_ids || [])[0] || "",
    });
    // include hint for title
    navigate(`/jobs/new?${params.toString()}`);
  };

  const bulkCreate = async () => {
    if (selected.size === 0 || bulkBusy) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/reports/actions-outstanding/create-follow-ups", {
        job_ids: Array.from(selected),
      });
      const nOk = (data.created || []).length;
      const nFail = (data.failed || []).length;
      if (nOk) toast.success(`Created ${nOk} follow-up job${nOk === 1 ? "" : "s"}`);
      if (nFail) toast.error(`${nFail} failed: ${(data.failed[0]?.error) || "unknown"}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBulkBusy(false); }
  };

  const exportParams = {};
  if (customerId !== "all") exportParams.customer_id = customerId;
  if (minAge !== "0") exportParams.min_age_days = minAge;

  return (
    <ReportPageShell
      testId="actions-outstanding"
      title="Actions Outstanding"
      subtitle="Closed jobs flagged as needing office follow-up, oldest first."
      headerActions={
        <div className="flex items-center gap-2">
          {selected.size > 0 && (
            <Button size="sm" onClick={bulkCreate} disabled={bulkBusy} data-testid="actions-outstanding-bulk-create">
              <Plus className="h-3.5 w-3.5" />
              {bulkBusy ? "Creating…" : `Create follow-up jobs (${selected.size})`}
            </Button>
          )}
          <ExportDropdown
            basePath="/reports/actions-outstanding"
            params={exportParams}
            filename="actions-outstanding"
            testId="actions-outstanding-export"
          />
        </div>
      }
      filters={
        <>
          <div className="w-56">
            <EntityCombobox
              items={customers}
              value={customerId === "all" ? "" : customerId}
              onChange={(v) => setCustomerId(v || "all")}
              placeholder="All customers"
              emptyMessage="No customers match."
              allowClear
              clearLabel="All customers"
              testid="actions-outstanding-customer"
              triggerClassName="h-8 text-xs"
            />
          </div>
          <Select value={minAge} onValueChange={setMinAge}>
            <SelectTrigger className="h-8 w-40 text-xs" data-testid="actions-outstanding-age"><SelectValue /></SelectTrigger>
            <SelectContent>
              {AGES.map((a) => (<SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>))}
            </SelectContent>
          </Select>
        </>
      }
      table={
        <table className="w-full text-sm" data-testid="actions-outstanding-table">
          <thead className="bg-muted/50 border-b border-border">
            <tr className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              <th className="text-left py-2 px-3 w-8">
                {items.length > 0 && (
                  <Checkbox
                    checked={selected.size === items.length}
                    onCheckedChange={toggleAll}
                    data-testid="actions-outstanding-select-all"
                  />
                )}
              </th>
              <th className="text-left py-2 px-3">Job</th>
              <th className="text-left py-2 px-3">Customer</th>
              <th className="text-left py-2 px-3">Site</th>
              <th className="text-left py-2 px-3">Engineer</th>
              <th className="text-left py-2 px-3">Closed</th>
              <th className="text-left py-2 px-3">Age</th>
              <th className="text-left py-2 px-3">Notes</th>
              <th className="text-right py-2 px-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="py-8 text-center text-xs text-muted-foreground">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={9} className="py-8 text-center text-xs text-muted-foreground" data-testid="actions-outstanding-empty">
                Nothing outstanding. Nice work.
              </td></tr>
            ) : items.map((r) => (
              <tr key={r.id} data-testid={`actions-outstanding-row-${r.id}`} className="border-b border-border last:border-0 hover:bg-muted/30">
                <td className="py-2 px-3">
                  <Checkbox
                    checked={selected.has(r.id)}
                    onCheckedChange={() => toggle(r.id)}
                    data-testid={`actions-outstanding-check-${r.id}`}
                  />
                </td>
                <td className="py-2 px-3">
                  <Link to={withBackHref(`/jobs/${r.id}`)} className="font-mono text-xs font-medium hover:text-primary" data-testid={`actions-outstanding-job-link-${r.id}`}>{r.job_number}</Link>
                  <div className="text-xs text-muted-foreground truncate max-w-[220px]" title={r.title}>{r.title}</div>
                </td>
                <td className="py-2 px-3 text-muted-foreground">{r.customer_name || "—"}</td>
                <td className="py-2 px-3 text-muted-foreground">{r.site_name || "—"}</td>
                <td className="py-2 px-3 text-muted-foreground">{r.engineer_name || "—"}</td>
                <td className="py-2 px-3 text-muted-foreground whitespace-nowrap">{formatDate(r.closed_at)}</td>
                <td className="py-2 px-3">
                  <StatusBadge tone={r.days_since_closed >= 30 ? "danger" : r.days_since_closed >= 7 ? "warning" : "info"}>
                    {r.days_since_closed}d
                  </StatusBadge>
                </td>
                <td className="py-2 px-3 text-xs text-muted-foreground truncate max-w-[260px]" title={r.action_notes}>
                  <AlertTriangle className="h-3 w-3 inline mr-1 text-amber-600" />
                  {r.action_notes || "—"}
                </td>
                <td className="py-2 px-3 text-right">
                  <Button size="sm" variant="outline" onClick={() => createOne(r)} data-testid={`actions-outstanding-create-${r.id}`}>
                    <Plus className="h-3.5 w-3.5" /> Follow-up
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      }
    />
  );
}
